import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';

/* The desk's side of payment links (/api/commerce): capabilities, links, sends.
   Shared by the link modal, the sale's "Collect online" panel and the
   Purchases page. */

export interface LinkCapabilities {
  onlinePayments: boolean;
  canSendLinkOnWhatsApp: boolean;
  canCopyLink: boolean;
  missing: Array<'online_payments' | 'payment_link_template'>;
}

export interface LinkMessage {
  triggerEvent: string;
  status: string;
  errorMessage: string | null;
  createdAt: string;
}

export interface PaymentLink {
  id: string;
  status: 'created' | 'sent' | 'paid' | 'expired' | 'cancelled';
  channel: string;
  amount: number;
  paidAmount: number | null;
  excessAmount: number;
  url: string;
  expiresAt: string;
  openedAt: string | null;
  lastSentAt: string | null;
  sendCount: number;
  paidAt: string | null;
  createdAt: string;
  messages?: LinkMessage[];
}

export async function commerceCall<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE_URL}/api/commerce${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.error || 'Request failed');
  return json.data as T;
}

/** Still takes money: open status and not past its expiry (the reconciler marks it later). */
export function isLinkOpen(l: Pick<PaymentLink, 'status' | 'expiresAt'>) {
  return (l.status === 'created' || l.status === 'sent') && new Date(l.expiresAt) > new Date();
}

export const fetchLinkCapabilities = (gymId: string) =>
  commerceCall<LinkCapabilities>(`/capabilities?gymId=${encodeURIComponent(gymId)}`);

/** Generate a link for these sales — or get back the open one the member already has. */
export const generatePaymentLink = (gymId: string, memberId: string, purchaseIds: string[]) =>
  commerceCall<PaymentLink>('/payment-requests', { method: 'POST', body: JSON.stringify({ gymId, memberId, purchaseIds }) });

export const sendPaymentLink = (gymId: string, linkId: string) =>
  commerceCall<void>(`/payment-requests/${linkId}/send`, { method: 'POST', body: JSON.stringify({ gymId }) });

export const linksForPurchase = (gymId: string, purchaseId: string) =>
  commerceCall<PaymentLink[]>(`/payment-requests?gymId=${encodeURIComponent(gymId)}&purchaseId=${encodeURIComponent(purchaseId)}`);
